import type { ReactNode } from 'react'
import type { ClinicDataAdapter } from '@/lib/clinic/storage'
import type {
    Appointment,
    AppointmentInput,
    AppointmentStatus,
    ClinicData,
    ClinicSettings,
    FollowUp,
    FollowUpEvent,
    FollowUpEventType,
    FollowUpInput,
    Invoice,
    InvoiceInput,
    MessageInput,
    MessageLog,
    Patient,
    PatientInput,
    PaymentInput,
    Prescription,
    PrescriptionInput,
    Treatment,
    TreatmentInput,
    Visit,
    VisitInput
} from '@/types/clinic'

import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react'
import { toast } from 'sonner'
import { createId } from '@/lib/format'
import { formatDate, todayISO } from '@/lib/dates'
import { createSeedData } from '@/lib/clinic/seed'
import { treatmentNames } from '@/lib/clinic/selectors'
import { localStorageAdapter, normalizeClinicData } from '@/lib/clinic/storage'
import { formatInvoiceNumber, formatPatientCode, formatPrescriptionNumber } from '@/lib/clinic/codes'
import { DATA_VERSION, DEFAULT_SETTINGS, STORAGE_KEY } from '@/constants/clinic'

export interface ClinicActions {
    addPatient: (input: PatientInput) => Patient
    updatePatient: (id: string, input: Partial<PatientInput>) => void
    deletePatient: (id: string) => void
    addVisit: (input: VisitInput) => Visit
    updateVisit: (id: string, input: Partial<VisitInput>) => void
    deleteVisit: (id: string) => void
    addTreatment: (input: TreatmentInput) => Treatment
    updateTreatment: (id: string, input: Partial<TreatmentInput>) => void
    deleteTreatment: (id: string) => void
    addAppointment: (input: AppointmentInput) => Appointment
    updateAppointment: (id: string, input: Partial<AppointmentInput>) => void
    setAppointmentStatus: (id: string, status: AppointmentStatus) => void
    deleteAppointment: (id: string) => void
    addPrescription: (input: PrescriptionInput, options?: { createFollowUp?: boolean }) => Prescription
    updatePrescription: (id: string, input: Partial<PrescriptionInput>, options?: { createFollowUp?: boolean }) => void
    deletePrescription: (id: string) => void
    addInvoice: (input: InvoiceInput, initialPayment?: PaymentInput | null) => Invoice
    updateInvoice: (id: string, input: Partial<InvoiceInput>) => void
    deleteInvoice: (id: string) => void
    addPayment: (invoiceId: string, payment: PaymentInput) => void
    deletePayment: (invoiceId: string, paymentId: string) => void
    addFollowUp: (input: FollowUpInput) => FollowUp
    updateFollowUp: (id: string, input: Partial<FollowUpInput>) => void
    rescheduleFollowUp: (id: string, dueDate: string, note?: string) => void
    completeFollowUp: (id: string, note?: string) => void
    reopenFollowUp: (id: string) => void
    deleteFollowUp: (id: string) => void
    logMessage: (input: MessageInput) => MessageLog
    updateMessage: (id: string, input: Partial<MessageInput>) => void
    deleteMessage: (id: string) => void
    updateSettings: <K extends keyof ClinicSettings>(section: K, patch: Partial<ClinicSettings[K]>) => void
    resetDemoData: () => void
    importData: (raw: unknown) => boolean
}

export interface ClinicContextValue {
    ready: boolean
    today: string
    data: ClinicData
    actions: ClinicActions
}

const EMPTY_DATA: ClinicData = {
    version: DATA_VERSION,
    seededOn: '',
    patients: [],
    treatments: [],
    visits: [],
    appointments: [],
    prescriptions: [],
    invoices: [],
    followUps: [],
    messages: [],
    settings: DEFAULT_SETTINGS,
    counters: { patient: 1000, prescription: 0, invoice: 0 }
}

const ClinicContext = createContext<ClinicContextValue | null>(null)

interface ClinicDataProviderProps {
    children: ReactNode
    adapter?: ClinicDataAdapter
}

export const ClinicDataProvider = ({ children, adapter = localStorageAdapter }: ClinicDataProviderProps): ReactNode => {
    const [data, setData] = useState<ClinicData>(EMPTY_DATA)
    const [ready, setReady] = useState(false)
    const [today, setToday] = useState('')
    const dataRef = useRef<ClinicData>(EMPTY_DATA)
    const todayRef = useRef('')

    const commit = useCallback((next: ClinicData) => {
        dataRef.current = next
        setData(next)
        adapter.save(next).catch(() => {
            toast.error('Changes could not be saved on this device', {
                description: 'Browser storage is full or blocked. Export your data from Settings to keep a copy.'
            })
        })
    }, [adapter])

    // Load saved data once, in the browser only. First visit gets the demo data.
    useEffect(() => {
        let cancelled = false
        const currentDay = todayISO()

        adapter.load()
            .catch(() => null)
            .then(saved => {
                if (cancelled) return
                const initial = saved ?? createSeedData(currentDay)
                dataRef.current = initial
                todayRef.current = currentDay
                setData(initial)
                setToday(currentDay)
                setReady(true)
                if (!saved) {
                    adapter.save(initial).catch(() => {
                        toast.error('Browser storage is unavailable', { description: 'You can use the demo, but changes will not be kept after a refresh.' })
                    })
                }
            })

        return () => {
            cancelled = true
        }
    }, [adapter])

    // Keep other open tabs in sync, and roll "today" over after midnight.
    useEffect(() => {
        const onStorage = (event: StorageEvent) => {
            if (event.key !== STORAGE_KEY || !event.newValue) return
            try {
                const parsed = normalizeClinicData(JSON.parse(event.newValue))
                if (parsed) {
                    dataRef.current = parsed
                    setData(parsed)
                }
            }
            catch {
                // Ignore values that are not clinic data.
            }
        }
        const onFocus = () => {
            const current = todayISO()
            if (todayRef.current && current !== todayRef.current) {
                todayRef.current = current
                setToday(current)
            }
        }

        window.addEventListener('storage', onStorage)
        window.addEventListener('focus', onFocus)
        document.addEventListener('visibilitychange', onFocus)
        return () => {
            window.removeEventListener('storage', onStorage)
            window.removeEventListener('focus', onFocus)
            document.removeEventListener('visibilitychange', onFocus)
        }
    }, [])

    const actions = useMemo<ClinicActions>(() => {
        const read = () => dataRef.current
        const now = () => new Date().toISOString()
        const currentDay = () => todayRef.current || todayISO()
        const event = (type: FollowUpEventType, note: string): FollowUpEvent => ({ id: createId('evt'), at: now(), type, note })

        const createFollowUpRecord = (input: FollowUpInput, note: string): FollowUp => {
            const stamp = now()
            return {
                ...input,
                id: createId('fu'),
                completedOn: input.status === 'completed' ? currentDay() : '',
                reminderCount: 0,
                lastReminderAt: '',
                history: [{ id: createId('evt'), at: stamp, type: 'created', note }],
                createdAt: stamp,
                updatedAt: stamp
            }
        }

        const prescriptionFollowUp = (current: ClinicData, prescription: Prescription): FollowUp | null => {
            if (!prescription.followUpDate) return null
            const exists = current.followUps.some(item => item.patientId === prescription.patientId
                && item.status === 'pending'
                && item.dueDate === prescription.followUpDate)
            if (exists) return null
            const visit = prescription.visitId ? current.visits.find(item => item.id === prescription.visitId) : undefined
            return createFollowUpRecord({
                patientId: prescription.patientId,
                visitId: prescription.visitId,
                treatment: visit ? treatmentNames(visit.treatments) : 'Prescription review',
                previousVisitDate: visit?.date ?? prescription.date,
                dueDate: prescription.followUpDate,
                notes: `Review after prescription ${prescription.number}`,
                status: 'pending'
            }, `Follow-up date set on prescription ${prescription.number}`)
        }

        return {
            addPatient: (input) => {
                const current = read()
                const sequence = current.counters.patient + 1
                const stamp = now()
                const patient: Patient = { ...input, id: createId('pt'), code: formatPatientCode(sequence), createdAt: stamp, updatedAt: stamp }
                commit({ ...current, patients: [patient, ...current.patients], counters: { ...current.counters, patient: sequence } })
                return patient
            },

            updatePatient: (id, input) => {
                const current = read()
                commit({ ...current, patients: current.patients.map(item => (item.id === id ? { ...item, ...input, updatedAt: now() } : item)) })
            },

            deletePatient: (id) => {
                const current = read()
                commit({
                    ...current,
                    patients: current.patients.filter(item => item.id !== id),
                    visits: current.visits.filter(item => item.patientId !== id),
                    appointments: current.appointments.filter(item => item.patientId !== id),
                    prescriptions: current.prescriptions.filter(item => item.patientId !== id),
                    invoices: current.invoices.filter(item => item.patientId !== id),
                    followUps: current.followUps.filter(item => item.patientId !== id),
                    messages: current.messages.filter(item => item.patientId !== id)
                })
            },

            addVisit: (input) => {
                const current = read()
                const stamp = now()
                const { followUpNotes, ...fields } = input
                const visit: Visit = { ...fields, id: createId('vis'), createdAt: stamp, updatedAt: stamp }

                let followUps = current.followUps.map((item): FollowUp => {
                    if (item.patientId === visit.patientId && item.status === 'pending' && item.dueDate <= visit.date) {
                        return {
                            ...item,
                            status: 'completed',
                            completedOn: visit.date,
                            updatedAt: stamp,
                            history: [...item.history, event('completed', `Patient visited on ${formatDate(visit.date)}`)]
                        }
                    }
                    return item
                })

                if (visit.nextVisitDate) {
                    followUps = [createFollowUpRecord({
                        patientId: visit.patientId,
                        visitId: visit.id,
                        treatment: treatmentNames(visit.treatments),
                        previousVisitDate: visit.date,
                        dueDate: visit.nextVisitDate,
                        notes: followUpNotes ?? '',
                        status: 'pending'
                    }, 'Next visit date set during the visit'), ...followUps]
                }

                const appointments = visit.appointmentId
                    ? current.appointments.map((item): Appointment => (item.id === visit.appointmentId ? { ...item, status: 'completed', updatedAt: stamp } : item))
                    : current.appointments

                commit({ ...current, visits: [visit, ...current.visits], followUps, appointments })
                return visit
            },

            updateVisit: (id, input) => {
                const current = read()
                const existing = current.visits.find(item => item.id === id)
                if (!existing) return
                const stamp = now()
                const { followUpNotes, ...fields } = input
                const visit: Visit = { ...existing, ...fields, updatedAt: stamp }

                let followUps = current.followUps
                const linked = followUps.find(item => item.visitId === id && item.status === 'pending')

                if (visit.nextVisitDate && linked) {
                    const moved = linked.dueDate !== visit.nextVisitDate
                    followUps = followUps.map((item): FollowUp => (item.id === linked.id
                        ? {
                            ...item,
                            dueDate: visit.nextVisitDate,
                            treatment: treatmentNames(visit.treatments),
                            previousVisitDate: visit.date,
                            notes: followUpNotes ?? item.notes,
                            updatedAt: stamp,
                            history: moved
                                ? [...item.history, event('rescheduled', `Moved from ${formatDate(linked.dueDate)} to ${formatDate(visit.nextVisitDate)}`)]
                                : item.history
                        }
                        : item))
                }
                else if (visit.nextVisitDate && !followUps.some(item => item.visitId === id && item.dueDate === visit.nextVisitDate)) {
                    followUps = [createFollowUpRecord({
                        patientId: visit.patientId,
                        visitId: visit.id,
                        treatment: treatmentNames(visit.treatments),
                        previousVisitDate: visit.date,
                        dueDate: visit.nextVisitDate,
                        notes: followUpNotes ?? '',
                        status: 'pending'
                    }, 'Next visit date added to the visit'), ...followUps]
                }
                else if (!visit.nextVisitDate && linked) {
                    followUps = followUps.filter(item => item.id !== linked.id)
                }

                commit({ ...current, visits: current.visits.map(item => (item.id === id ? visit : item)), followUps })
            },

            deleteVisit: (id) => {
                const current = read()
                commit({
                    ...current,
                    visits: current.visits.filter(item => item.id !== id),
                    followUps: current.followUps
                        .filter(item => !(item.visitId === id && item.status === 'pending'))
                        .map(item => (item.visitId === id ? { ...item, visitId: null } : item)),
                    invoices: current.invoices.map(item => (item.visitId === id ? { ...item, visitId: null } : item)),
                    prescriptions: current.prescriptions.map(item => (item.visitId === id ? { ...item, visitId: null } : item))
                })
            },

            addTreatment: (input) => {
                const current = read()
                const stamp = now()
                const treatment: Treatment = { ...input, id: createId('trt'), createdAt: stamp, updatedAt: stamp }
                commit({ ...current, treatments: [...current.treatments, treatment] })
                return treatment
            },

            updateTreatment: (id, input) => {
                const current = read()
                commit({ ...current, treatments: current.treatments.map(item => (item.id === id ? { ...item, ...input, updatedAt: now() } : item)) })
            },

            deleteTreatment: (id) => {
                const current = read()
                commit({
                    ...current,
                    treatments: current.treatments.filter(item => item.id !== id),
                    appointments: current.appointments.map(item => (item.treatmentId === id ? { ...item, treatmentId: null } : item)),
                    visits: current.visits.map(item => ({
                        ...item,
                        treatments: item.treatments.map(ref => (ref.treatmentId === id ? { ...ref, treatmentId: null } : ref))
                    })),
                    invoices: current.invoices.map(item => ({
                        ...item,
                        items: item.items.map(line => (line.treatmentId === id ? { ...line, treatmentId: null } : line))
                    }))
                })
            },

            addAppointment: (input) => {
                const current = read()
                const stamp = now()
                const appointment: Appointment = { ...input, id: createId('apt'), createdAt: stamp, updatedAt: stamp }
                commit({ ...current, appointments: [...current.appointments, appointment] })
                return appointment
            },

            updateAppointment: (id, input) => {
                const current = read()
                commit({ ...current, appointments: current.appointments.map(item => (item.id === id ? { ...item, ...input, updatedAt: now() } : item)) })
            },

            setAppointmentStatus: (id, status) => {
                const current = read()
                commit({ ...current, appointments: current.appointments.map(item => (item.id === id ? { ...item, status, updatedAt: now() } : item)) })
            },

            deleteAppointment: (id) => {
                const current = read()
                commit({
                    ...current,
                    appointments: current.appointments.filter(item => item.id !== id),
                    visits: current.visits.map(item => (item.appointmentId === id ? { ...item, appointmentId: null } : item)),
                    messages: current.messages.map(item => (item.appointmentId === id ? { ...item, appointmentId: null } : item))
                })
            },

            addPrescription: (input, options) => {
                const current = read()
                const sequence = current.counters.prescription + 1
                const stamp = now()
                const prescription: Prescription = {
                    ...input,
                    id: createId('rx'),
                    number: formatPrescriptionNumber(sequence, input.date || currentDay()),
                    createdAt: stamp,
                    updatedAt: stamp
                }
                const followUp = options?.createFollowUp ? prescriptionFollowUp(current, prescription) : null
                commit({
                    ...current,
                    prescriptions: [prescription, ...current.prescriptions],
                    followUps: followUp ? [followUp, ...current.followUps] : current.followUps,
                    counters: { ...current.counters, prescription: sequence }
                })
                return prescription
            },

            updatePrescription: (id, input, options) => {
                const current = read()
                const existing = current.prescriptions.find(item => item.id === id)
                if (!existing) return
                const prescription: Prescription = { ...existing, ...input, updatedAt: now() }
                const followUp = options?.createFollowUp ? prescriptionFollowUp(current, prescription) : null
                commit({
                    ...current,
                    prescriptions: current.prescriptions.map(item => (item.id === id ? prescription : item)),
                    followUps: followUp ? [followUp, ...current.followUps] : current.followUps
                })
            },

            deletePrescription: (id) => {
                const current = read()
                commit({ ...current, prescriptions: current.prescriptions.filter(item => item.id !== id) })
            },

            addInvoice: (input, initialPayment) => {
                const current = read()
                const sequence = current.counters.invoice + 1
                const stamp = now()
                const payments = initialPayment && initialPayment.amount > 0
                    ? [{ ...initialPayment, id: createId('pay'), createdAt: stamp }]
                    : []
                const invoice: Invoice = {
                    ...input,
                    id: createId('inv'),
                    number: formatInvoiceNumber(current.settings.billing.invoicePrefix, sequence, input.date || currentDay()),
                    payments,
                    createdAt: stamp,
                    updatedAt: stamp
                }
                commit({ ...current, invoices: [invoice, ...current.invoices], counters: { ...current.counters, invoice: sequence } })
                return invoice
            },

            updateInvoice: (id, input) => {
                const current = read()
                commit({ ...current, invoices: current.invoices.map(item => (item.id === id ? { ...item, ...input, updatedAt: now() } : item)) })
            },

            deleteInvoice: (id) => {
                const current = read()
                commit({
                    ...current,
                    invoices: current.invoices.filter(item => item.id !== id),
                    messages: current.messages.map(item => (item.invoiceId === id ? { ...item, invoiceId: null } : item))
                })
            },

            addPayment: (invoiceId, payment) => {
                const current = read()
                const stamp = now()
                commit({
                    ...current,
                    invoices: current.invoices.map(item => (item.id === invoiceId
                        ? { ...item, payments: [...item.payments, { ...payment, id: createId('pay'), createdAt: stamp }], updatedAt: stamp }
                        : item))
                })
            },

            deletePayment: (invoiceId, paymentId) => {
                const current = read()
                commit({
                    ...current,
                    invoices: current.invoices.map(item => (item.id === invoiceId
                        ? { ...item, payments: item.payments.filter(payment => payment.id !== paymentId), updatedAt: now() }
                        : item))
                })
            },

            addFollowUp: (input) => {
                const current = read()
                const followUp = createFollowUpRecord(input, 'Follow-up added')
                commit({ ...current, followUps: [followUp, ...current.followUps] })
                return followUp
            },

            updateFollowUp: (id, input) => {
                const current = read()
                commit({
                    ...current,
                    followUps: current.followUps.map((item): FollowUp => {
                        if (item.id !== id) return item
                        const moved = input.dueDate !== undefined && input.dueDate !== item.dueDate
                        return {
                            ...item,
                            ...input,
                            updatedAt: now(),
                            history: [...item.history, moved
                                ? event('rescheduled', `Moved from ${formatDate(item.dueDate)} to ${formatDate(input.dueDate ?? '')}`)
                                : event('updated', 'Follow-up details updated')]
                        }
                    })
                })
            },

            rescheduleFollowUp: (id, dueDate, note) => {
                const current = read()
                commit({
                    ...current,
                    followUps: current.followUps.map((item): FollowUp => (item.id === id
                        ? {
                            ...item,
                            dueDate,
                            status: 'pending',
                            completedOn: '',
                            updatedAt: now(),
                            history: [...item.history, event('rescheduled', note?.trim()
                                ? `Moved from ${formatDate(item.dueDate)} to ${formatDate(dueDate)}. ${note.trim()}`
                                : `Moved from ${formatDate(item.dueDate)} to ${formatDate(dueDate)}`)]
                        }
                        : item))
                })
            },

            completeFollowUp: (id, note) => {
                const current = read()
                commit({
                    ...current,
                    followUps: current.followUps.map((item): FollowUp => (item.id === id
                        ? {
                            ...item,
                            status: 'completed',
                            completedOn: currentDay(),
                            updatedAt: now(),
                            history: [...item.history, event('completed', note?.trim() || 'Marked as completed')]
                        }
                        : item))
                })
            },

            reopenFollowUp: (id) => {
                const current = read()
                commit({
                    ...current,
                    followUps: current.followUps.map((item): FollowUp => (item.id === id
                        ? { ...item, status: 'pending', completedOn: '', updatedAt: now(), history: [...item.history, event('reopened', 'Follow-up reopened')] }
                        : item))
                })
            },

            deleteFollowUp: (id) => {
                const current = read()
                commit({
                    ...current,
                    followUps: current.followUps.filter(item => item.id !== id),
                    messages: current.messages.map(item => (item.followUpId === id ? { ...item, followUpId: null } : item))
                })
            },

            logMessage: (input) => {
                const current = read()
                const stamp = now()
                const message: MessageLog = { ...input, id: createId('msg'), createdAt: stamp, sentAt: input.status === 'sent' ? stamp : '' }
                const followUps = input.status === 'sent' && input.followUpId
                    ? current.followUps.map((item): FollowUp => (item.id === input.followUpId
                        ? { ...item, reminderCount: item.reminderCount + 1, lastReminderAt: stamp, updatedAt: stamp, history: [...item.history, event('reminder', 'WhatsApp reminder opened for sending')] }
                        : item))
                    : current.followUps
                commit({ ...current, messages: [message, ...current.messages], followUps })
                return message
            },

            updateMessage: (id, input) => {
                const current = read()
                const existing = current.messages.find(item => item.id === id)
                if (!existing) return
                const stamp = now()
                const becameSent = input.status === 'sent' && existing.status !== 'sent'
                const message: MessageLog = { ...existing, ...input, sentAt: becameSent ? stamp : existing.sentAt }
                const followUpId = message.followUpId
                const followUps = becameSent && followUpId
                    ? current.followUps.map((item): FollowUp => (item.id === followUpId
                        ? { ...item, reminderCount: item.reminderCount + 1, lastReminderAt: stamp, updatedAt: stamp, history: [...item.history, event('reminder', 'WhatsApp reminder opened for sending')] }
                        : item))
                    : current.followUps
                commit({ ...current, messages: current.messages.map(item => (item.id === id ? message : item)), followUps })
            },

            deleteMessage: (id) => {
                const current = read()
                commit({ ...current, messages: current.messages.filter(item => item.id !== id) })
            },

            updateSettings: (section, patch) => {
                const current = read()
                commit({ ...current, settings: { ...current.settings, [section]: { ...current.settings[section], ...patch } } })
            },

            resetDemoData: () => {
                const day = todayISO()
                todayRef.current = day
                setToday(day)
                commit(createSeedData(day))
            },

            importData: (raw) => {
                const parsed = normalizeClinicData(raw)
                if (!parsed) return false
                commit(parsed)
                return true
            }
        }
    }, [commit])

    const value = useMemo<ClinicContextValue>(() => ({ ready, today, data, actions }), [ready, today, data, actions])

    return (
        <ClinicContext.Provider value={value}>
            {children}
        </ClinicContext.Provider>
    )
}

export const useClinic = (): ClinicContextValue => {
    const context = useContext(ClinicContext)
    if (!context) throw new Error('useClinic must be used inside ClinicDataProvider')
    return context
}
